import {
  type ISourceEntity,
  ISourceEntityTypes,
  SourceEntityMigrationType,
} from '@principle-theorem/practice-migrations/interfaces';
import { SourceEntity } from '../../../source/source-entity';
import { OFFSET_PLACEHOLDER } from '../../../source/source-helpers';
import {
  ISO_DATE_TIME_FORMAT,
  Timestamp,
  toTimestamp,
  TypeGuard,
  type Timezone,
} from '@principle-theorem/shared';
import { flow, isNumber, isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import * as moment from 'moment-timezone';

export const UNUSED_STERILISATION_RECORD_RESOURCE_TYPE =
  'unusedSterilisationRecord';

export const UNUSED_STERILISATION_RECORD_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Unused Sterilisation Record List',
      description: '',
      idPrefix: UNUSED_STERILISATION_RECORD_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

const UNUSED_STERILISATION_ESTIMATE_QUERY = `
SELECT 1
FROM ast_packs p
INNER JOIN ast_pack_cycle pc ON pc.id_pack = p.id_pack
INNER JOIN ast_cycles c ON c.id_cycle = pc.id_cycle
INNER JOIN ast_sterilizers s ON s.id_sterilizer = c.id_sterilizer
WHERE NOT EXISTS (
  SELECT 1
  FROM ast_pack_treatment t
  WHERE t.id_pack = p.id_pack
)
`;

const UNUSED_STERILISATION_RECORDS_SOURCE_QUERY = `
SELECT
  sterilisation_pack.pack_id,
  sterilisation_pack.barcode,
  sterilisation_pack.contents,
  sterilisation_pack.date_sterilised,
  sterilisation_pack.staff_id,
  sterilisation_pack.pack_content_id,
  pack_cycle.cycle_id,
  steriliser.practice_id,
  steriliser.steriliser_id
FROM (
  SELECT
    id_pack AS pack_id,
    convert_to_integer(ast_pack_contents_list_id) AS pack_content_id,
    bar_code::TEXT AS barcode,
    contents,
    id_staff AS staff_id,
    sync_date AS date_sterilised,
    used
  FROM ast_packs
  ORDER BY id_pack
  ${OFFSET_PLACEHOLDER}
) AS sterilisation_pack
INNER JOIN (
  SELECT
    id_pack,
    id_cycle AS cycle_id
  FROM ast_pack_cycle
) AS pack_cycle
  ON convert_to_text(pack_cycle.id_pack) = convert_to_text(sterilisation_pack.pack_id)
INNER JOIN (
  SELECT
    id_cycle,
    id_sterilizer
  FROM ast_cycles
) AS cycle
  ON convert_to_text(cycle.id_cycle) = convert_to_text(pack_cycle.cycle_id)
INNER JOIN (
  SELECT
    id_practice AS practice_id,
    id_sterilizer AS steriliser_id
  FROM ast_sterilizers
) AS steriliser
  ON convert_to_text(steriliser.steriliser_id) = convert_to_text(cycle.id_sterilizer)
LEFT JOIN (
  SELECT id_pack
  FROM ast_pack_treatment
) AS used ON convert_to_text(used.id_pack) = convert_to_text(sterilisation_pack.pack_id)
WHERE used.id_pack IS NULL
`;

export interface ID4WUnusedSterilisationRecord {
  pack_id: number;
  barcode: string;
  contents: string;
  date_sterilised: string;
  staff_id: number;
  pack_content_id: number | null;
  cycle_id: number;
  practice_id: number;
  steriliser_id: number;
}

export function isD4WUnusedSterilisationRecord(
  record: unknown,
  logError: boolean
): record is ID4WUnusedSterilisationRecord {
  return TypeGuard.interface<ID4WUnusedSterilisationRecord>(
    {
      pack_id: isNumber,
      barcode: isString,
      contents: isString,
      date_sterilised: isString,
      staff_id: isNumber,
      pack_content_id: TypeGuard.nilOr(isNumber),
      cycle_id: isNumber,
      practice_id: isNumber,
      steriliser_id: isNumber,
    },
    logError
  )(record);
}

export interface ID4WUnusedSterilisationRecordTranslations {
  dateSterilised: Timestamp;
}

export type D4WUnusedSterilisationRecord = ISourceEntityTypes<
  ID4WUnusedSterilisationRecord,
  ID4WUnusedSterilisationRecordTranslations
>;

export class UnusedSterilisationRecordSourceEntity extends BaseSourceEntity<D4WUnusedSterilisationRecord> {
  sourceEntity = UNUSED_STERILISATION_RECORD_SOURCE_ENTITY;
  entityResourceType = UNUSED_STERILISATION_RECORD_RESOURCE_TYPE;
  sourceQuery = UNUSED_STERILISATION_RECORDS_SOURCE_QUERY;
  override estimateQuery = UNUSED_STERILISATION_ESTIMATE_QUERY;
  verifySourceFn = isD4WUnusedSterilisationRecord;
  override transformDataFn = flow([]);
  override defaultOffsetSize = 25000;

  migrationDestinations = [UNUSED_STERILISATION_RECORD_RESOURCE_TYPE];

  translate(
    data: ID4WUnusedSterilisationRecord,
    timezone: Timezone
  ): ID4WUnusedSterilisationRecordTranslations {
    return {
      dateSterilised: toTimestamp(
        moment.tz(data.date_sterilised, ISO_DATE_TIME_FORMAT, timezone)
      ),
    };
  }

  getSourceRecordId(data: ID4WUnusedSterilisationRecord): number {
    return data.pack_id;
  }

  getSourceLabel(data: ID4WUnusedSterilisationRecord): string {
    return `${data.pack_id}`;
  }
}
